import { useState } from 'react'
import api from '../../lib/api'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { motion } from 'framer-motion'
import { Check, Loader2, ChevronLeft } from 'lucide-react'
import {
  StripePaymentElement,
  PaymentElementSuccess,
} from '@/components/billing/StripePaymentElement'

type TierSlug = 'associate' | 'partner' | 'principal'
type Interval = 'monthly' | 'annual'

interface PlanSelectStepProps {
  onComplete: (data: { tier: TierSlug; interval: Interval; subscriptionId: string }) => void
  onBack?: () => void
}

const TIERS: Array<{
  slug: TierSlug
  name: string
  monthly: number
  annual: number
  promise: string
  highlights: string[]
  mostPopular?: boolean
}> = [
  {
    slug: 'associate',
    name: 'Associate',
    monthly: 397,
    annual: 3970,
    promise: 'A full-service AI intake layer for your website.',
    highlights: [
      'Tailored AI chat trained on your website + knowledge base',
      'Bookings on your calendar, live staff takeover',
      'Team management, chat history, custom alerts',
      'Analytics dashboard for the whole practice',
    ],
  },
  {
    slug: 'partner',
    name: 'Partner',
    monthly: 897,
    annual: 8970,
    mostPopular: true,
    promise: 'Intake that never sleeps — web and phone.',
    highlights: [
      'Everything in Associate',
      'AI answers every call, 24/7',
      'Dedicated number, 250 calls/mo',
      'Transcripts, summaries, caller history',
    ],
  },
  {
    slug: 'principal',
    name: 'Principal',
    monthly: 1497,
    annual: 14970,
    promise: 'Every inquiry becomes a conversation.',
    highlights: [
      'Everything in Partner',
      'Proactive callback on every web inquiry',
      'CRM sync (Clio, Lawmatics, HubSpot, more)',
      '600 calls/mo, reminder calls, priority support',
    ],
  },
]

export default function PlanSelectStep({ onComplete, onBack }: PlanSelectStepProps) {
  const [selectedTier, setSelectedTier] = useState<TierSlug | null>(null)
  const [interval, setInterval] = useState<Interval>('monthly')
  const [confirming, setConfirming] = useState(false)
  const { toast } = useToast()

  const handlePaymentSuccess = async (result: PaymentElementSuccess) => {
    if (!selectedTier) return
    setConfirming(true)
    try {
      const res = await api.post('/api/billing/checkout/confirm', {
        setupIntentId: result.setupIntentId,
        paymentMethodId: result.paymentMethodId,
        tier: selectedTier,
        interval,
      })
      if (res.data.success) {
        onComplete({
          tier: selectedTier,
          interval,
          subscriptionId: res.data.data.subscriptionId,
        })
      } else {
        throw new Error(res.data.message || 'Subscription creation failed')
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Something went wrong'
      toast({ title: 'Checkout failed', description: msg, variant: 'destructive' })
      setConfirming(false)
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-0">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
      >
        {/* Header */}
        <div className="mb-8 text-center lg:text-left">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-periwinkle/80 mb-3">
            Step 03 &mdash; Choose your coverage
          </p>
          <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-white tracking-tight leading-[1.05]">
            Pick your coverage.
            <br className="hidden sm:inline" />
            <span className="text-white/60">Live intake, ready in minutes.</span>
          </h2>
          <p className="text-white/50 mt-4 text-sm leading-relaxed max-w-xl">
            Month-to-month, cancel anytime. Every plan includes the AI chat trained on your firm.
            Higher tiers add 24/7 phone answering and proactive callbacks.
          </p>
        </div>

        {!selectedTier && (
          <>
            {/* Interval toggle */}
            <div className="flex items-center gap-3 justify-center lg:justify-start mb-6">
              <button
                type="button"
                onClick={() => setInterval('monthly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  interval === 'monthly'
                    ? 'bg-white/10 text-white'
                    : 'text-white/50 hover:text-white/80'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setInterval('annual')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  interval === 'annual'
                    ? 'bg-white/10 text-white'
                    : 'text-white/50 hover:text-white/80'
                }`}
              >
                Annual
                <span className="ml-2 text-[10px] text-brand-periwinkle">2 months free</span>
              </button>
            </div>

            {/* Tier cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              {TIERS.map((tier) => (
                <motion.button
                  key={tier.slug}
                  type="button"
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => setSelectedTier(tier.slug)}
                  className={`relative text-left rounded-2xl border p-6 transition-all ${
                    tier.mostPopular
                      ? 'border-brand-periwinkle/40 bg-gradient-to-b from-brand-royal/10 to-transparent'
                      : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
                  }`}
                >
                  {tier.mostPopular && (
                    <span className="absolute -top-2 right-4 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white shadow-sm shadow-brand-periwinkle/30">
                      Most Popular
                    </span>
                  )}
                  <h3 className="text-lg font-semibold text-white mb-1">{tier.name}</h3>
                  <p className="text-xs text-white/50 mb-4 min-h-[40px]">{tier.promise}</p>
                  <div className="mb-5">
                    <span className="text-3xl font-bold text-white">
                      ${interval === 'monthly' ? tier.monthly : Math.round(tier.annual / 12)}
                    </span>
                    <span className="text-sm text-white/50 ml-1">/mo</span>
                    {interval === 'annual' && (
                      <p className="text-[11px] text-white/40 mt-1">
                        ${tier.annual.toLocaleString()} billed annually
                      </p>
                    )}
                  </div>
                  <ul className="space-y-2">
                    {tier.highlights.map((h) => {
                      const checkColor = tier.slug === 'associate'
                        ? 'text-sky-400'
                        : tier.slug === 'partner'
                          ? 'text-brand-periwinkle'
                          : 'text-violet-400'
                      return (
                        <li key={h} className="flex items-start gap-2 text-xs text-white/70">
                          <Check className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${checkColor}`} strokeWidth={2.5} />
                          <span>{h}</span>
                        </li>
                      )
                    })}
                  </ul>
                </motion.button>
              ))}
            </div>

            {onBack && (
              <Button
                variant="ghost"
                onClick={onBack}
                className="!text-white/55 hover:!text-white text-sm font-medium"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Go back
              </Button>
            )}
          </>
        )}

        {selectedTier && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 max-w-4xl">
            {/* Left: selection summary */}
            <div>
              <button
                type="button"
                onClick={() => setSelectedTier(null)}
                className="text-xs text-white/50 hover:text-white flex items-center gap-1 mb-4"
              >
                <ChevronLeft className="h-3 w-3" />
                Change plan
              </button>
              {(() => {
                const tier = TIERS.find((t) => t.slug === selectedTier)!
                return (
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                    <h3 className="text-lg font-semibold text-white mb-1">{tier.name}</h3>
                    <p className="text-xs text-white/50 mb-4">{tier.promise}</p>
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-white">
                        ${interval === 'monthly' ? tier.monthly : Math.round(tier.annual / 12)}
                      </span>
                      <span className="text-sm text-white/50 ml-1">/mo</span>
                      {interval === 'annual' && (
                        <p className="text-[11px] text-white/40 mt-1">
                          ${tier.annual.toLocaleString()} billed annually
                        </p>
                      )}
                    </div>
                    <ul className="space-y-2">
                      {tier.highlights.map((h) => (
                        <li key={h} className="flex items-start gap-2 text-xs text-white/70">
                          <Check className="h-3.5 w-3.5 text-brand-periwinkle mt-0.5 shrink-0" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              })()}
            </div>

            {/* Right: embedded payment element */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <h3 className="text-sm font-semibold text-white mb-4">Payment details</h3>
              {confirming ? (
                <div className="flex items-center justify-center py-12 text-white/60">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  <span className="text-sm">Starting your coverage…</span>
                </div>
              ) : (
                <StripePaymentElement
                  tier={selectedTier}
                  interval={interval}
                  onSuccess={handlePaymentSuccess}
                  submitLabel="Start my coverage"
                />
              )}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  )
}
